import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Forms, Loading, SystemText, TitleBar } from '@salesperformanceint/e4shared';
import Filters from '../_filters';
import GroupPicker from 'components/admin/forms/groupPicker';
import MetricPicker from 'components/admin/forms/metricPicker';
import PeriodPicker from 'components/admin/forms/periodPicker';
import CompetencyPicker from 'components/admin/forms/competencyPicker';
import WidgetBase, { groupSet, metricSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import BarChart from './barChart';
import Staff from './staff';
import { sortGoalsByPeriod } from 'helpers/goals';
import Correlation from '../components/correlation';
import CompetencyRecommendations from '../components/competencyRecommendations';
import GoalPicker from 'components/admin/forms/goalPicker';

export const Matrix = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const goals = useSelector(state => state.dashboard.goals);
    const mappedGoals = sortGoalsByPeriod(goals, true);

    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [periodOptions1, setPeriodOptions1] = React.useState(null);
    const [periodOptions2, setPeriodOptions2] = React.useState(null);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        widgetLoad(item.id, user.guid, validator, setLoading, setNotConfigured, setData, () => {
            widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
        });
    };

    const updatePeriodOptions = () => {
        if (values?.goal1) {
            let goal = goals.find(g => g.id === values.goal1);
            const options = [goal?.period];
            if (!options?.some(o => values.period1.charAt(0) === o)) {
                updateProperty(`${goal?.period}1`, 'period1');
            }

            setPeriodOptions1(options);
        } else {
            setPeriodOptions1(null);
        }

        if (values?.goal2) {
            let goal = goals.find(g => g.id === values.goal2);
            const options = [goal?.period];
            if (!options?.some(o => values.period2.charAt(0) === o)) {
                updateProperty(`${goal?.period}1`, 'period2');
            }

            setPeriodOptions2(options);
        } else {
            setPeriodOptions2(null);
        }
    };

    React.useEffect(() => {
        updatePeriodOptions();
    }, [goals, values?.goal1, values?.goal2, values?.period1, values?.period2]);

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;

        if (prop === 'type1') {
            temp.metric1 = value === 'M' ? temp.metric1 : null;
            temp.competency1 = value === 'C' ? temp.competency1 : null;
            temp.goal1 = value === 'G' ? temp.goal1 : null;
        }

        if (prop === 'type2') {
            temp.metric2 = value === 'M' ? temp.metric2 : null;
            temp.competency2 = value === 'C' ? temp.competency2 : null;
            temp.goal2 = value === 'G' ? temp.goal2 : null;
        }

        setValues(temp);
    };

    const height = defaultProps.height(item);

    const defaults = { type1: 'C', type2: 'C', show_For: true, period1: 'M1', period2: 'M1', groupBy: 'P' };

    const groupByData = ['B', 'D', 'L', 'P', 'M']
        .map(x => ({ id: x, name: SystemText(`Dashboard/Widgets/General/Fields/GroupDataBy/Types/${x}`) }))
        .sort((a, b) => (a > b ? 1 : -1));

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!form.groupBy) {
            errors.groupBy = SystemText('Dashboard/Widgets/General/Fields/GroupDataBy/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/Matrix/Fields/Group/Error');
            validForm = false;
        }

        if (form.type1 === 'M' && !metricSet(form.metric1)) {
            errors.metric1 = SystemText('Dashboard/Widgets/Matrix/Fields/XAxis/Errors/Metric');
            validForm = false;
        }

        if (form.type2 === 'M' && !metricSet(form.metric2)) {
            errors.metric2 = SystemText('Dashboard/Widgets/Matrix/Fields/YAxis/Errors/Metric');
            validForm = false;
        }

        if (form.type1 === 'G' && !form.goal1) {
            errors.goal1 = SystemText('Dashboard/Widgets/Matrix/Fields/XAxis/Errors/Goal');
            validForm = false;
        }

        if (form.type2 === 'G' && !form.goal2) {
            errors.goal2 = SystemText('Dashboard/Widgets/Matrix/Fields/YAxis/Errors/Goal');
            validForm = false;
        }

        if (
            form.type1 === 'M' &&
            form.metric1?.subMetricId &&
            (!form.metric1?.subMetricValues || form.metric1?.subMetricValues.length === 0)
        ) {
            errors.metric1 = SystemText('Dashboard/Widgets/Matrix/Fields/XAxis/Errors/Options');
            validForm = false;
        }

        if (
            form.type2 === 'M' &&
            form.metric2?.subMetricId &&
            (!form.metric2?.subMetricValues || form.metric2?.subMetricValues.length === 0)
        ) {
            errors.metric2 = SystemText('Dashboard/Widgets/Matrix/Fields/YAxis/Errors/Options');
            validForm = false;
        }

        if (values.type1 !== 'C' && !form.period1) {
            errors.period1 = SystemText('Dashboard/Widgets/Matrix/Fields/Period1/Error');
            validForm = false;
        }

        if (values.type2 !== 'C' && !form.period2) {
            errors.period2 = SystemText('Dashboard/Widgets/Matrix/Fields/Period2/Error');
            validForm = false;
        }

        if (form.type1 === 'C' && !form.competency1?.areaId && form.competency1?.areaId !== -1) {
            errors.competency1 = SystemText('Dashboard/Widgets/Matrix/Fields/XAxis/Errors/Competency');
            validForm = false;
        }

        if (form.type2 === 'C' && !form.competency2?.areaId && form.competency2?.areaId !== -1) {
            errors.competency2 = SystemText('Dashboard/Widgets/Matrix/Fields/YAxis/Errors/Competency');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <div className="chart" style={{ height: `${height}px` }}>
                <BarChart item={item} />
            </div>
            <div className="chart" style={{ height: `${height}px` }}>
                <Staff data={data} item={item} id={`chart-${item.id}`} />
            </div>
        </div>
    );

    const recommendations = () => {
        if (recLoading) return <Loading />;

        const result = [];

        ArrayHelpers.existsAndHasLength(recommendationData?.bestcomps) &&
            result.push(
                <CompetencyRecommendations
                    text={SystemText('Dashboard/Widgets/Matrix/Recommends/Strongest')}
                    comps={recommendationData?.bestcomps}
                />
            );

        ArrayHelpers.existsAndHasLength(recommendationData?.worstcomps) &&
            result.push(
                <CompetencyRecommendations
                    text={SystemText('Dashboard/Widgets/Matrix/Recommends/Weakest')}
                    comps={recommendationData?.worstcomps}
                />
            );

        recommendationData?.correlation && result.push(<Correlation widget={item} value={recommendationData.correlation} />);

        return result;
    };

    const isRealTime = metric => {
        return metric?.metricRef.indexOf('RT-') >= 0;
    };

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <div className="clear-titles padded-titles">
                <Forms.Select
                    label={SystemText('Dashboard/Widgets/Matrix/Fields/GroupDataBy/Label')}
                    help={SystemText('Dashboard/Widgets/Matrix/Fields/GroupDataBy/Help')}
                    mandatory
                    value={values.groupBy}
                    error={errors.groupBy}
                    data={groupByData}
                    onChange={value => updateProperty(value, 'groupBy')}
                />
                <GroupPicker
                    mandatory
                    allowMultiple
                    value={values.group1}
                    error={errors.group1}
                    onChange={value => updateProperty(value, 'group1')}
                    showUserOption={defaultProps.company}
                    controlId={id}
                />
                <TitleBar title={SystemText('Dashboard/Widgets/Matrix/XAxis')} />
                <Forms.Buttons label={SystemText('Dashboard/Widgets/Matrix/Fields/XAxis/Label')}>
                    <button
                        className={`btn ${values.type1 === 'C' ? 'btn-primary' : ''}`}
                        onClick={() => updateProperty('C', 'type1')}
                    >
                        {SystemText('Dashboard/Widgets/Matrix/Fields/Axis/Toggle/Competency')}
                    </button>
                    <button
                        className={`btn ${values.type1 === 'M' ? 'btn-primary' : ''}`}
                        onClick={() => updateProperty('M', 'type1')}
                    >
                        {SystemText('Dashboard/Widgets/Matrix/Fields/Axis/Toggle/Metric')}
                    </button>
                    <button
                        className={`btn ${values.type1 === 'G' ? 'btn-primary' : ''}`}
                        onClick={() => updateProperty('G', 'type1')}
                    >
                        {SystemText('Dashboard/Widgets/Matrix/Fields/Axis/Toggle/Goal')}
                    </button>
                </Forms.Buttons>
                {values.type1 === 'C' && (
                    <>
                        <CompetencyPicker
                            label={SystemText('Dashboard/Widgets/Matrix/Fields/XAxisCompetency/Label')}
                            mandatory
                            value={values.competency1}
                            error={errors.competency1}
                            onChange={value => updateProperty(value, 'competency1')}
                        />
                        <Forms.Toggle
                            label={SystemText('Dashboard/Widgets/Matrix/Fields/XAxisCompetencyTrueScale/Label')}
                            help={SystemText('Dashboard/Widgets/Matrix/Fields/XAxisCompetencyTrueScale/Help')}
                            value={values.competencyTrueScale1}
                            yesLabel={SystemText('Dashboard/Widgets/Matrix/Fields/CompetencyTrueScale/Yes')}
                            noLabel={SystemText('Dashboard/Widgets/Matrix/Fields/CompetencyTrueScale/No')}
                            onChange={value => updateProperty(value, 'competencyTrueScale1')}
                        />
                    </>
                )}
                {values.type1 === 'G' && (
                    <GoalPicker
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/XAxisGoal/Label')}
                        mandatory
                        value={values.goal1}
                        error={errors.goal1}
                        data={mappedGoals}
                        onChange={value => {
                            updateProperty(value, 'goal1');
                        }}
                    />
                )}
                {values.type1 === 'M' && (
                    <MetricPicker
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/XAxisMetric/Label')}
                        mandatory
                        value={values.metric1}
                        error={errors.metric1}
                        onChange={value => updateProperty(value, 'metric1')}
                        allowRealtime
                    />
                )}
                {values.type1 !== 'C' && !isRealTime(values.metric1) && (
                    <PeriodPicker
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/Period1/Label')}
                        mandatory
                        limitTypes={periodOptions1}
                        value={values.period1 || values.period}
                        error={errors.period1}
                        metric={values.metric1?.type}
                        onChange={value => updateProperty(value, 'period1')}
                        help={SystemText('Dashboard/Widgets/Matrix/Fields/Period1/Help')}
                    />
                )}
                <TitleBar title={SystemText('Dashboard/Widgets/Matrix/YAxis')} />
                <Forms.Buttons label={SystemText('Dashboard/Widgets/Matrix/Fields/YAxis/Label')}>
                    <button
                        className={`btn ${values.type2 === 'C' ? 'btn-primary' : ''}`}
                        onClick={() => updateProperty('C', 'type2')}
                    >
                        {SystemText('Dashboard/Widgets/Matrix/Fields/Axis/Toggle/Competency')}
                    </button>
                    <button
                        className={`btn ${values.type2 === 'M' ? 'btn-primary' : ''}`}
                        onClick={() => updateProperty('M', 'type2')}
                    >
                        {SystemText('Dashboard/Widgets/Matrix/Fields/Axis/Toggle/Metric')}
                    </button>
                    <button
                        className={`btn ${values.type2 === 'G' ? 'btn-primary' : ''}`}
                        onClick={() => updateProperty('G', 'type2')}
                    >
                        {SystemText('Dashboard/Widgets/Matrix/Fields/Axis/Toggle/Goal')}
                    </button>
                </Forms.Buttons>
                {values.type2 === 'C' && (
                    <>
                        <CompetencyPicker
                            label={SystemText('Dashboard/Widgets/Matrix/Fields/YAxisCompetency/Label')}
                            mandatory
                            value={values.competency2}
                            error={errors.competency2}
                            onChange={value => updateProperty(value, 'competency2')}
                        />
                        <Forms.Toggle
                            label={SystemText('Dashboard/Widgets/Matrix/Fields/YAxisCompetencyTrueScale/Label')}
                            help={SystemText('Dashboard/Widgets/Matrix/Fields/YAxisCompetencyTrueScale/Help')}
                            value={values.competencyTrueScale2}
                            yesLabel={SystemText('Dashboard/Widgets/Matrix/Fields/CompetencyTrueScale/Yes')}
                            noLabel={SystemText('Dashboard/Widgets/Matrix/Fields/CompetencyTrueScale/No')}
                            onChange={value => updateProperty(value, 'competencyTrueScale2')}
                        />
                    </>
                )}
                {values.type2 === 'G' && (
                    <GoalPicker
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/YAxisGoal/Label')}
                        mandatory
                        value={values.goal2}
                        error={errors.goal2}
                        data={mappedGoals}
                        onChange={value => {
                            updateProperty(value, 'goal2');
                        }}
                    />
                )}
                {values.type2 === 'M' && (
                    <MetricPicker
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/YAxisMetric/Label')}
                        mandatory
                        value={values.metric2}
                        error={errors.metric2}
                        onChange={value => updateProperty(value, 'metric2')}
                        allowRealtime
                    />
                )}
                {values.type2 !== 'C' && !isRealTime(values.metric2) && (
                    <PeriodPicker
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/Period2/Label')}
                        mandatory
                        limitTypes={periodOptions2}
                        value={values.period2 || values.period}
                        error={errors.period2}
                        metric={values.metric2?.type}
                        onChange={value => updateProperty(value, 'period2')}
                        help={SystemText('Dashboard/Widgets/Matrix/Fields/Period2/Help')}
                    />
                )}
                <TitleBar
                    title={SystemText('Admin/General/More')}
                    data-testid="span-show-more"
                    openTitle={SystemText('Admin/General/Less')}
                    className="showMore"
                >
                    <Forms.Text
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/Legend1/Label')}
                        help={SystemText('Dashboard/Widgets/Matrix/Fields/Legend1/Help')}
                        placeholder={SystemText('Dashboard/Widgets/Matrix/Fields/Legend1/Placeholder')}
                        maxLength="50"
                        value={values.legend1}
                        onChange={value => updateProperty(value, 'legend1')}
                    />
                    <Forms.Text
                        label={SystemText('Dashboard/Widgets/Matrix/Fields/Legend2/Label')}
                        help={SystemText('Dashboard/Widgets/Matrix/Fields/Legend2/Help')}
                        placeholder={SystemText('Dashboard/Widgets/Matrix/Fields/Legend2/Placeholder')}
                        maxLength="50"
                        value={values.legend2}
                        onChange={value => updateProperty(value, 'legend2')}
                    />
                </TitleBar>
            </div>
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations()}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default Matrix;
